"use client";

import {
	type ColumnFiltersState,
	type PaginationState,
	type SortingState,
	type TableOptions,
	type TableState,
	type VisibilityState,
	type Updater,
	getCoreRowModel,
	getFacetedRowModel,
	getFacetedUniqueValues,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { usePathname, useSearchParams } from "next/navigation";
import * as React from "react";

import type { DataTableFilterField } from "@/components/ui/data-table/types";
import { StorageType, useStorage } from "@/features/projects/hooks/use-storage";

interface UseDataTableProps<TData>
	extends Omit<
		TableOptions<TData>,
		| "pageCount"
		| "getCoreRowModel"
		| "manualFiltering"
		| "manualPagination"
		| "manualSorting"
		| "onPaginationChange"
		| "onSortingChange"
	>,
	Required<Pick<TableOptions<TData>, "pageCount">> {
	filterFields?: DataTableFilterField<TData>[];
	enableAdvancedFilter?: boolean;
	storageKey?: string;
	onRefetch?: () => void;
	initialState?: Omit<Partial<TableState>, "sorting"> & {
		sorting?: {
			id: Extract<keyof TData, string>;
			desc: boolean;
		}[];
	};
}

interface TableStorageState {
	page: number;
	pageSize: number;
	sorting: SortingState;
	pathname: string;
}

export function useDataTable<TData>({
	pageCount = -1,
	filterFields = [],
	enableAdvancedFilter = false,
	storageKey = "data-table-state",
	onRefetch,
	...props
}: UseDataTableProps<TData>) {
	const storage = useStorage(StorageType.Session);
	const pathname = usePathname();
	const searchParams = useSearchParams();

	// console.log('[useDataTable] Hook inicializado');

	const urlKey = React.useMemo(() => {
		return `${pathname}-${searchParams.toString()}`;
	}, [pathname, searchParams]);

	const [lastUrlKey, setLastUrlKey] = React.useState(urlKey);

	React.useEffect(() => {
		if (lastUrlKey !== urlKey) {
			// console.log('[useDataTable] URL cambió - limpiando storage');
			storage.remove(storageKey);
			storage.remove(`${storageKey}-filters`);
			setLastUrlKey(urlKey);
		}
	}, [urlKey, lastUrlKey, storage, storageKey]);

	const loadInitialState = React.useCallback((): TableStorageState => {
		const savedState = storage.get<TableStorageState>(storageKey);

		if (savedState && savedState.pathname !== pathname) {
			console.log("[useDataTable] Limpiando storage por cambio de ruta");
			storage.remove(storageKey);
			storage.remove(`${storageKey}-filters`);
		}

		const defaultState: TableStorageState = {
			page: 1,
			pageSize: props.initialState?.pagination?.pageSize || 10,
			sorting: props.initialState?.sorting ? [{
				id: props.initialState.sorting[0].id,
				desc: props.initialState.sorting[0].desc,
			}] : [],
			pathname,
		};

		const finalState = (savedState && savedState.pathname === pathname) ? savedState : defaultState;
		// console.log('[useDataTable] Estado inicial cargado:', finalState);

		return finalState;
	}, [storage, storageKey, pathname, props.initialState]);

	const initialState = loadInitialState();

	const [rowSelection, setRowSelection] = React.useState({});
	const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
	const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
	const [pagination, setPagination] = React.useState<PaginationState>({
		pageIndex: initialState.page - 1,
		pageSize: initialState.pageSize,
	});
	const [sorting, setSorting] = React.useState<SortingState>(initialState.sorting);

	const syncTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

	React.useEffect(() => {
		const syncWithStorage = () => {
			const savedState = storage.get<TableStorageState>(storageKey);
			if (savedState && savedState.pathname === pathname) {
				const newPageIndex = savedState.page - 1;

				setPagination(current => {
					if (current.pageIndex !== newPageIndex || current.pageSize !== savedState.pageSize) {
						// console.log('[useDataTable] Sincronizando con Session Storage:', {
						// 	anteriorPageIndex: current.pageIndex,
						// 	nuevoPageIndex: newPageIndex,
						// 	anteriorPageSize: current.pageSize,
						// 	nuevoPageSize: savedState.pageSize
						// });
						return {
							pageIndex: newPageIndex,
							pageSize: savedState.pageSize
						};
					}
					return current;
				});
			}
		};

		syncWithStorage();
		const interval = setInterval(syncWithStorage, 50);

		return () => {
			clearInterval(interval);
			if (syncTimeoutRef.current) {
				clearTimeout(syncTimeoutRef.current);
			}
		};
	}, [storage, storageKey, pathname]);

	React.useEffect(() => {
		if (pageCount > 0 && pagination.pageIndex >= pageCount) {
			//  console.log('[useDataTable] Página actual excede total de páginas - corrigiendo:', {
			// 	pageIndex: pagination.pageIndex,
			// 	pageCount,
			// 	ajustandoA: pageCount - 1
			// });

			const correctedPageIndex = Math.max(0, pageCount - 1);

			setPagination(prev => ({
				...prev,
				pageIndex: correctedPageIndex
			}));

			const stateToSave: TableStorageState = {
				page: correctedPageIndex + 1,
				pageSize: pagination.pageSize,
				sorting,
				pathname,
			};

			storage.set(storageKey, stateToSave);

			if (syncTimeoutRef.current) {
				clearTimeout(syncTimeoutRef.current);
			}

			syncTimeoutRef.current = setTimeout(() => {
				if (onRefetch) {
					// console.log('[useDataTable] Ejecutando refetch después de corrección de página');
					onRefetch();
				}
			}, 100);
		}
	}, [pageCount, pagination.pageIndex, pagination.pageSize, sorting, pathname, storage, storageKey, onRefetch]);

	// console.log('[useDataTable] Estado de paginacion:', {
	// 	pageIndex: pagination.pageIndex,
	// 	pageSize: pagination.pageSize,
	// 	pageCount
	// });

	const handlePaginationChange = React.useCallback(
		(updater: Updater<PaginationState>) => {
			// console.log('[useDataTable] handlePaginationChange llamado');

			setPagination((currentPagination) => {
				const newPagination = typeof updater === "function" ? updater(currentPagination) : updater;

				// console.log('[useDataTable] Cambio de paginacion:', {
				// 	anterior: currentPagination,
				// 	nuevo: newPagination
				// });

				const stateToSave: TableStorageState = {
					page: newPagination.pageIndex + 1,
					pageSize: newPagination.pageSize,
					sorting,
					pathname,
				};

				console.log("[useDataTable] Guardando estado de paginacion:", stateToSave);
				storage.set(storageKey, stateToSave);

				console.log("[useDataTable] onRefetch disponible?", !!onRefetch);
				if (onRefetch) {
					console.log("[useDataTable] Ejecutando onRefetch por cambio de paginacion");
					if (syncTimeoutRef.current) {
						clearTimeout(syncTimeoutRef.current);
					}
					syncTimeoutRef.current = setTimeout(() => {
						console.log("[useDataTable] Llamando onRefetch ahora...");
						onRefetch();
					}, 0);
				}

				return newPagination;
			});
		},
		[sorting, pathname, storage, storageKey, onRefetch]
	);

	const handleSortingChange = React.useCallback(
		(updater: Updater<SortingState>) => {
			// console.log('[useDataTable] handleSortingChange llamado');

			setSorting((currentSorting) => {
				const newSorting = typeof updater === "function" ? updater(currentSorting) : updater;

				// console.log('[useDataTable] Cambio de sorting:', {
				// 	anterior: currentSorting,
				// 		nuevo: newSorting
				// });

				setPagination({
					pageIndex: 0,
					pageSize: pagination.pageSize
				});

				const stateToSave: TableStorageState = {
					page: 1,
					pageSize: pagination.pageSize,
					sorting: newSorting,
					pathname,
				};

				console.log("[useDataTable] Guardando estado de sorting con reset a pagina 1:", stateToSave);
				storage.set(storageKey, stateToSave);

				console.log("[useDataTable] onRefetch disponible?", !!onRefetch);
				if (onRefetch) {
					console.log("[useDataTable] Ejecutando onRefetch por cambio de sorting");
					if (syncTimeoutRef.current) {
						clearTimeout(syncTimeoutRef.current);
					}
					syncTimeoutRef.current = setTimeout(() => {
						console.log("[useDataTable] Llamando onRefetch ahora...");
						onRefetch();
					}, 0);
				}

				return newSorting;
			});
		},
		[pagination.pageSize, pathname, storage, storageKey, onRefetch]
	);

	const handleColumnFiltersChange = React.useCallback(
		(updater: Updater<ColumnFiltersState>) => {
			// console.log('[useDataTable] handleColumnFiltersChange llamado');

			setColumnFilters((currentFilters) => {
				const newFilters = typeof updater === "function" ? updater(currentFilters) : updater;

				// console.log('[useDataTable] Cambio de filtros:', {
				// 	anterior: currentFilters,
				// 		nuevo: newFilters
				// });

				setPagination({
					pageIndex: 0,
					pageSize: pagination.pageSize
				});

				const stateToSave: TableStorageState = {
					page: 1,
					pageSize: pagination.pageSize,
					sorting,
					pathname,
				};

				console.log("[useDataTable] Reseteando a pagina 1 y guardando estado:", stateToSave);
				storage.set(storageKey, stateToSave);

				console.log("[useDataTable] onRefetch disponible?", !!onRefetch);
				if (onRefetch) {
					console.log("[useDataTable] Ejecutando onRefetch por cambio de filtros");
					if (syncTimeoutRef.current) {
						clearTimeout(syncTimeoutRef.current);
					}
					syncTimeoutRef.current = setTimeout(() => {
						console.log("[useDataTable] Llamando onRefetch ahora...");
						onRefetch();
					}, 0);
				}

				return newFilters;
			});
		},
		[pagination.pageSize, sorting, pathname, storage, storageKey, onRefetch]
	);

	React.useEffect(() => {
		const currentPathname = pathname;
		return () => {
			const savedState = storage.get<TableStorageState>(storageKey);
			if (savedState && savedState.pathname !== currentPathname) {
				// console.log('[useDataTable] Limpieza por desmontaje - pathname cambio');
				storage.remove(storageKey);
				storage.remove(`${storageKey}-filters`);
			}
		};
	}, [pathname, storage, storageKey]);

	const table = useReactTable({
		...props,
		pageCount,
		state: {
			pagination,
			sorting,
			columnVisibility,
			rowSelection,
			columnFilters,
		},
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		onPaginationChange: handlePaginationChange,
		onSortingChange: handleSortingChange,
		onColumnFiltersChange: handleColumnFiltersChange,
		onColumnVisibilityChange: setColumnVisibility,
		getCoreRowModel: getCoreRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFacetedRowModel: getFacetedRowModel(),
		getFacetedUniqueValues: getFacetedUniqueValues(),
		manualPagination: true,
		manualSorting: true,
		manualFiltering: true,
		enableColumnResizing: true,
	});

	const resetTableState = React.useCallback(() => {
		// console.log('[useDataTable] Reseteando estado completo de la tabla');
		storage.remove(storageKey);
		storage.remove(`${storageKey}-filters`);
		setPagination({ pageIndex: 0, pageSize: initialState.pageSize });
		setSorting([]);
		setColumnFilters([]);
		setRowSelection({});
		if (onRefetch) {
			// console.log('[useDataTable] Ejecutando onRefetch por reset');
			onRefetch();
		}
	}, [storage, storageKey, initialState.pageSize, onRefetch]);

	return {
		table,
		canPreviousPage: pagination.pageIndex > 0,
		canNextPage: pageCount > 0 ? pagination.pageIndex < pageCount - 1 : false,
		resetTableState,
	};
}